import React from "react";
import { useTranslation } from "../../hooks/useTranslation";
import type { TextBlock } from "./types";

interface StoryCanvasControlsProps {
  onAddText: () => void;
  onAddImage: () => void;
  onDeleteSelected: () => void;
  hasSelection: boolean;
  selectedTextBlock: TextBlock | null;
  onUpdateTextBlock: (id: string, updates: Partial<TextBlock>) => void;
}

const TEXT_COLORS = [
  "#ffffff",
  "#000000",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
];

/** Minimal story controls: add image, add text, and when text is selected: font size, color, and delete. */
const StoryCanvasControls: React.FC<StoryCanvasControlsProps> = ({
  onAddText,
  onAddImage,
  onDeleteSelected,
  hasSelection,
  selectedTextBlock,
  onUpdateTextBlock,
}) => {
  const { t } = useTranslation();
  const minFontSize = 14;
  const maxFontSize = 56;

  return (
    <div className="absolute bottom-0 left-0 right-0 z-20">
      <div className="bg-gradient-to-t from-black/95 via-black/90 to-transparent pb-safe">
        {/* When text is selected: color, font size +/- and delete */}
        {selectedTextBlock && (
          <>
            <div className="px-4 pt-3 pb-2 border-t border-white/10 flex flex-wrap items-center gap-3">
              <span className="text-white/70 text-xs font-medium w-full">{t("story.textColor")}</span>
              <div className="flex items-center gap-2 flex-wrap">
                {TEXT_COLORS.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    onClick={() => onUpdateTextBlock(selectedTextBlock.id, { color: hex })}
                    className="w-8 h-8 rounded-full border-2 border-white/40 touch-manipulation active:scale-95 shrink-0"
                    style={{ backgroundColor: hex }}
                    aria-label={t("story.colorAria", { hex })}
                    title={hex}
                  />
                ))}
                <label className="relative w-8 h-8 rounded-full border-2 border-white/40 overflow-hidden cursor-pointer shrink-0 bg-white/10 flex items-center justify-center">
                  <input
                    type="color"
                    value={selectedTextBlock.color || "#ffffff"}
                    onChange={(e) => onUpdateTextBlock(selectedTextBlock.id, { color: e.target.value })}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    aria-label={t("story.selectColor")}
                  />
                  <span className="pointer-events-none text-white/80 text-sm font-bold">+</span>
                </label>
              </div>
            </div>
            <div className="px-4 pt-2 pb-2 border-t border-white/10 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-xs font-medium">{t("story.size")}</span>
                <button
                  type="button"
                  onClick={() =>
                    onUpdateTextBlock(selectedTextBlock.id, {
                      fontSize: Math.max(minFontSize, (selectedTextBlock.fontSize ?? 24) - 2),
                    })
                  }
                  className="w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center text-lg font-bold touch-manipulation active:scale-95"
                  aria-label={t("story.decreaseSize")}
                >
                  −
                </button>
                <span className="text-white text-sm min-w-[2ch]">{(selectedTextBlock.fontSize ?? 24)}</span>
                <button
                  type="button"
                  onClick={() =>
                    onUpdateTextBlock(selectedTextBlock.id, {
                      fontSize: Math.min(maxFontSize, (selectedTextBlock.fontSize ?? 24) + 2),
                    })
                  }
                  className="w-9 h-9 rounded-full bg-white/20 text-white flex items-center justify-center text-lg font-bold touch-manipulation active:scale-95"
                  aria-label={t("story.increaseSize")}
                >
                  +
                </button>
              </div>
              <button
                type="button"
                onClick={onDeleteSelected}
                className="p-2 rounded-full bg-red-500/80 text-white touch-manipulation active:scale-95"
                aria-label={t("story.deleteText")}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </>
        )}

        {/* Main actions: Add image, Add text */}
        <div className="px-4 py-3 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={onAddImage}
            className="flex-1 max-w-[160px] px-4 py-3 rounded-xl bg-white/15 text-white font-medium text-sm flex items-center justify-center gap-2 touch-manipulation active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {t("story.addImage")}
          </button>
          <button
            type="button"
            onClick={onAddText}
            className="flex-1 max-w-[160px] px-4 py-3 rounded-xl bg-white/15 text-white font-medium text-sm flex items-center justify-center gap-2 touch-manipulation active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
            </svg>
            {t("story.addText")}
          </button>
          {hasSelection && !selectedTextBlock && (
            <button
              type="button"
              onClick={onDeleteSelected}
              className="p-3 rounded-xl bg-red-500/80 text-white touch-manipulation active:scale-95"
              aria-label={t("common.delete")}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoryCanvasControls;
